import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, EyeOff, Plus, Scale, TrendingDown } from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import {
  Button,
  Card,
  EmptyState,
  Page,
  PageHeader,
  Pill,
  SearchInput,
  SegmentedControl,
  Select,
  Skeleton,
  StatGrid,
} from '../../components/ui';
import { BalanceStrip, PositionCounts } from '../../features/issues/components/BalanceBar';
import { LockMarker } from '../../features/confidentiality';
import { IssueFormDialog } from '../../features/issues/components/IssueFormDialog';
import { PositionPill } from '../../features/issues/components/PositionMark';
import {
  ISSUE_SORTS,
  ISSUE_STATUSES,
  IssueSort,
  STATUS_LABEL,
  decisionIsNear,
  decisionPhrase,
  formatDate,
  formatIndex,
  issueHref,
  movementSummary,
  priorityLabel,
  sortIssues,
  statusToneOf,
} from '../../features/issues/components/format';
import {
  IssueFilters,
  useIssueQuery,
  useIssues,
  useIssuesOverview,
  useMovementTally,
} from '../../features/issues/useIssues';
import { useDebounce } from '../../hooks/useDebounce';
import { asOneOf, useUrlState } from '../../hooks/useUrlState';
import type { IssueStatus, IssueView } from '../../types/issues';

type StatusFilter = IssueStatus | 'all';

/* One codec per parameter, made once: the URL state reads them on every render */
const STATUS = asOneOf<StatusFilter>(['all', ...ISSUE_STATUSES]);
const SORT = asOneOf(ISSUE_SORTS.map((option) => option.value));

/**
 * The portfolio: every issue being tracked, where each one stands, how close
 * its decision is and which way it has been moving.
 */
export const IssuesListPage: React.FC = () => {
  // Which issues are on screen, and in what order, is in the address bar: a
  // link hands someone else the same portfolio, filters and all
  const [status, setStatus] = useUrlState<StatusFilter>('status', 'all', STATUS);
  const [category, setCategory] = useUrlState('category', '');
  const [ownerId, setOwnerId] = useUrlState('owner', '');
  const [q, setQ] = useUrlState('q', '');
  const [sort, setSort] = useUrlState<IssueSort>('sort', 'priority', SORT);
  const [creating, setCreating] = useState(false);

  // The box answers every keystroke; the URL catches up once typing stops
  const [searchInput, setSearchInput] = useState(q);
  const settledSearch = useDebounce(searchInput, 300);
  useEffect(() => {
    if (settledSearch !== q) setQ(settledSearch);
  }, [settledSearch, q, setQ]);

  const filters = useMemo<IssueFilters>(
    () => ({ status, category, ownerId, search: q }),
    [status, category, ownerId, q],
  );
  // The search box is already debounced on its way to the URL, so the query
  // takes what the address bar says without waiting a second time
  const query = useIssueQuery(filters, 0);
  const { issues, loading, error, unavailable, reload } = useIssues(query);
  const overview = useIssuesOverview();
  const movement = useMovementTally(14);

  const sorted = useMemo(() => sortIssues(issues, sort), [issues, sort]);

  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    issues.forEach((issue) => {
      if (issue.category) seen.set(issue.category, issue.categoryLabel || issue.category);
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [issues]);

  const owners = useMemo(() => {
    const seen = new Map<string, string>();
    issues.forEach((issue) => {
      if (issue.owner) seen.set(issue.owner.id, issue.owner.name);
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [issues]);

  /**
   * Both lists are drawn from the issues on screen, so a filter that matches
   * nothing would take its own dropdown away with it and leave no way back.
   * A value the list has lost keeps its place until it is cleared.
   */
  const withCurrent = (options: Array<{ value: string; label: string }>, value: string, label: string) =>
    value && !options.some((option) => option.value === value) ? [...options, { value, label }] : options;

  const categoryOptions = withCurrent(categories, category, category);
  const ownerOptions = withCurrent(owners, ownerId, 'Selected owner');

  const stats = overview.overview;
  const nextDecision = stats?.decisionsSoon?.[0];
  const worst = stats?.deteriorating?.[0];

  const statusItems = useMemo(
    () => [
      { value: 'all' as StatusFilter, label: 'All' },
      ...ISSUE_STATUSES.map((status) => ({
        value: status as StatusFilter,
        label: `${STATUS_LABEL[status]}${stats?.byStatus?.[status] ? ` ${stats.byStatus[status]}` : ''}`,
      })),
    ],
    [stats],
  );

  if (unavailable) {
    return (
      <DashboardLayout>
        <Page>
          <PageHeader title="Issues" />
          <Card>
            <EmptyState icon={Scale} title="Issues are not available on this server yet — the backend needs to be redeployed" />
          </Card>
        </Page>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Page>
        <PageHeader
          title="Issues"
          meta={loading && !issues.length ? 'Loading…' : `${issues.length} tracked`}
          actions={
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              New issue
            </Button>
          }
        />

        <StatGrid
          className="mb-4"
          items={[
            {
              label: 'Active',
              value: stats?.byStatus?.active ?? 0,
              hint: `${stats?.total ?? 0} tracked`,
            },
            {
              label: 'Decisions soon',
              icon: <CalendarClock className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />,
              value: stats?.decisionsSoon?.length ?? 0,
              hint: nextDecision ? `${nextDecision.title} · ${decisionPhrase(nextDecision.decisionDate)}` : 'None dated',
              ...(nextDecision ? { to: `/issues/${nextDecision.slug}` } : {}),
            },
            {
              label: 'Deteriorating',
              icon: <TrendingDown className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />,
              value: stats?.deteriorating?.length ?? 0,
              hint: worst ? `${worst.issue.title} · ${formatIndex(worst.change)}` : 'Balance holding',
              ...(worst ? { to: `/issues/${worst.issue.slug}` } : {}),
            },
            {
              label: 'Blind spots',
              icon: <EyeOff className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />,
              value: stats?.unassessed ?? 0,
              hint: 'No position recorded',
            },
          ]}
        />

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SearchInput
            className="w-full sm:w-64"
            placeholder="Search issues"
            aria-label="Search issues"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <SegmentedControl
            label="Status"
            items={statusItems}
            value={status}
            onChange={(value) => setStatus(value as StatusFilter)}
          />
          {categoryOptions.length > 0 && (
            <Select
              aria-label="Category"
              className="w-40"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="">All categories</option>
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
          {ownerOptions.length > 0 && (
            <Select
              aria-label="Owner"
              className="w-40"
              value={ownerId}
              onChange={(event) => setOwnerId(event.target.value)}
            >
              <option value="">All owners</option>
              {ownerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
          <SegmentedControl
            label="Sort"
            className="sm:ml-auto"
            items={ISSUE_SORTS}
            value={sort}
            onChange={setSort}
          />
        </div>

        {error && (
          <p role="alert" className="mb-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}{' '}
            <button
              type="button"
              onClick={reload}
              className="underline decoration-current/40 underline-offset-2 hover:decoration-current"
            >
              Try again
            </button>
          </p>
        )}

        {loading && !issues.length ? (
          <Card padded className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </Card>
        ) : !sorted.length ? (
          <Card>
            <EmptyState
              icon={Scale}
              title={issues.length ? 'Nothing matches these filters' : 'No issues yet'}
              action={
                !issues.length ? (
                  <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
                    Track the first issue
                  </Button>
                ) : undefined
              }
            />
          </Card>
        ) : (
          <Card divided>
            {sorted.map((issue) => (
              <IssueRow
                key={issue.id}
                issue={issue}
                moved={movement.tally[issue.id]?.moved ?? issue.recentMovements ?? 0}
                towards={movement.tally[issue.id]?.towards ?? 0}
              />
            ))}
          </Card>
        )}

        {creating && (
          <IssueFormDialog
            categories={categories}
            onClose={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              reload();
              overview.reload();
            }}
          />
        )}
      </Page>
    </DashboardLayout>
  );
};

const IssueRow: React.FC<{ issue: IssueView; moved: number; towards: number }> = ({ issue, moved, towards }) => {
  const near = decisionIsNear(issue.decisionDate);
  const summary = movementSummary(moved, towards);

  return (
    <Link to={issueHref(issue)} className="block px-4 py-3 transition-colors hover:bg-neutral-50">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-[13px] text-ink">{issue.title}</span>
            <LockMarker record={issue} />
            <Pill tone={statusToneOf(issue.status)}>{STATUS_LABEL[issue.status]}</Pill>
            {issue.priority >= 4 && <Pill tone="outline">{priorityLabel(issue.priority)}</Pill>}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-neutral-400">
            {issue.categoryLabel && <span className="truncate">{issue.categoryLabel}</span>}
            {issue.categoryLabel && issue.owner && <span aria-hidden="true">·</span>}
            {issue.owner && <span className="truncate">{issue.owner.name}</span>}
            {issue.decisionDate && (
              <>
                {(issue.categoryLabel || issue.owner) && <span aria-hidden="true">·</span>}
                <span className={near ? 'text-primary-700' : undefined} title={formatDate(issue.decisionDate)}>
                  Decision {decisionPhrase(issue.decisionDate)}
                </span>
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3 lg:w-[250px] lg:flex-shrink-0">
          <div className="min-w-0 flex-1">
            <BalanceStrip balance={issue.balance} />
            <PositionCounts balance={issue.balance} compact className="mt-1.5" />
          </div>
          <div className="w-11 flex-shrink-0 text-right">
            <span className="block text-[15px] leading-none tabular-nums text-ink" title="Balance of forces">
              {formatIndex(issue.balance?.index ?? 0)}
            </span>
            <span className="mt-1 block text-[11px] text-neutral-400">index</span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 lg:w-[210px] lg:flex-shrink-0 lg:justify-end">
          <span className="truncate whitespace-nowrap text-[12px] text-neutral-500" title={summary || undefined}>
            {summary || <span className="text-neutral-300">No movement</span>}
          </span>
          <PositionPill position={issue.ourPosition} short className="flex-shrink-0" />
        </div>
      </div>
    </Link>
  );
};

export default IssuesListPage;
